import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { SEO_LANDING_PAGES } from '../mock/seoLandingPagesData';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import { BrandsTicker } from '../components/Marquees';
import { Testimonials } from '../components/FaqTestimonials';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  ChevronRight,
  ArrowRight,
  Sparkles,
  Truck,
  ShieldCheck,
  Clock,
  Package,
  CheckCircle,
  HelpCircle,
  ShoppingBag,
} from 'lucide-react';

const iconMap = {
  Sparkles,
  Truck,
  ShieldCheck,
  Clock,
  Package,
};

const SeoLandingPage = ({ pageSlug }) => {
  const page = SEO_LANDING_PAGES[pageSlug];

  if (!page) {
    return <Navigate to="/corporate-gifting" replace />;
  }

  // 1. Generate BreadcrumbList Schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: page.breadcrumbs.map((b, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: b.name,
      item: `https://wrapyourwish.com${b.path}`,
    })),
  };

  // 2. Generate FAQPage Schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: page.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a,
      },
    })),
  };

  // 3. Generate Product Schema for featured hampers
  const productSchemas = page.featuredProducts.map((p) => {
    const rawPrice = p.price.replace(/[^0-9]/g, '');
    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: p.name,
      image: p.img,
      description: p.desc || p.name,
      sku: p.id,
      brand: {
        '@type': 'Brand',
        name: 'WrapYourWish',
      },
      offers: {
        '@type': 'Offer',
        url: `https://wrapyourwish.com${page.canonicalPath}#${p.id}`,
        priceCurrency: 'INR',
        price: rawPrice || '1499',
        priceValidUntil: '2026-12-31',
        itemCondition: 'https://schema.org/NewCondition',
        availability: 'https://schema.org/InStock',
        seller: {
          '@type': 'Organization',
          name: 'WrapYourWish',
        },
      },
    };
  });

  const schemas = [breadcrumbSchema, faqSchema, ...productSchemas];

  // Silo cross-links
  const otherPages = Object.values(SEO_LANDING_PAGES).filter(
    (p) => p.slug !== page.slug
  );

  return (
    <main className="bg-white">
      {/* Injected Dynamic Head SEO */}
      <SEO
        title={page.title}
        description={page.metaDescription}
        keywords={page.keywords}
        canonicalUrl={`https://wrapyourwish.com${page.canonicalPath}`}
        ogImage={page.heroImage}
        schemas={schemas}
      />

      {/* Breadcrumbs Navigation */}
      <div className="bg-[#FAF6F1] border-b border-[#EFE3D2]">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#7A5C4C]"
          >
            {page.breadcrumbs.map((b, i) => (
              <React.Fragment key={b.path}>
                {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-[#C58B76]" />}
                {i === page.breadcrumbs.length - 1 ? (
                  <span className="font-semibold text-[#2C2416]">{b.name}</span>
                ) : (
                  <Link
                    to={b.path}
                    className="hover:text-[#C58B76] transition-colors"
                  >
                    {b.name}
                  </Link>
                )}
              </React.Fragment>
            ))}
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-[#FAF6F1] overflow-hidden py-14 md:py-20 border-b border-[#E9DFD3]">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 -left-10 w-72 h-72 rounded-full pastel-blush opacity-70 blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full pastel-sage opacity-60 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-[#F4D5C5] text-[#7A5C4C] px-4 py-1.5 rounded-full text-xs uppercase tracking-widest">
                <Sparkles className="w-3.5 h-3.5" /> {page.badge}
              </div>
              <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#2C2416] leading-[1.08] mt-5">
                {page.heroH1}
              </h1>
              <p className="text-base sm:text-lg text-[#5B4A3A] mt-5 max-w-2xl leading-relaxed">
                {page.heroSubtitle}
              </p>

              <div className="flex flex-wrap gap-4 mt-8">
                <a
                  href="#enquire"
                  className="btn-primary px-7 py-3 rounded-full text-xs uppercase tracking-widest inline-flex items-center gap-2"
                >
                  Request Quote &amp; Samples <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="#featured-hampers"
                  className="btn-outline px-7 py-3 rounded-full text-xs uppercase tracking-widest"
                >
                  Browse Hampers
                </a>
              </div>

              {/* Highlights Bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10 pt-8 border-t border-[#E9DFD3]">
                {page.highlights.map((h, i) => (
                  <div key={i} className="bg-white/80 backdrop-blur rounded-xl p-3.5 border border-[#EFE3D2]">
                    <div className="text-[11px] uppercase tracking-widest text-[#7A5C4C]">
                      {h.label}
                    </div>
                    <div className="font-serif-display text-lg text-[#2C2416] font-semibold mt-1">
                      {h.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                <img
                  src={page.heroImage}
                  alt={page.heroH1}
                  width="600"
                  height="750"
                  fetchPriority="high"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl p-4 border border-[#EFE3D2] shadow-md flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#F4D5C5] flex items-center justify-center flex-shrink-0 text-[#2C2416]">
                    <CheckCircle className="w-5 h-5 text-[#C58B76]" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-[#2C2416]">
                      GST Invoicing &amp; Sample Kits
                    </div>
                    <div className="text-xs text-[#7A5C4C]">
                      Dispatching to 28,000+ Indian Pincodes
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Propositions / Why Choose */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.24em] text-[#C58B76] mb-3">
              Tailored Excellence
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#2C2416] leading-tight">
              {page.valuePropsTitle}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {page.valueProps.map((vp, i) => {
              const IconComponent = iconMap[vp.icon] || Sparkles;
              return (
                <div
                  key={i}
                  className="bg-[#FAF6F1] rounded-2xl p-6 border border-[#EFE3D2] hover:border-[#C58B76] transition-all hover:shadow-md flex flex-col justify-between"
                >
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-[#F4D5C5]/60 flex items-center justify-center mb-5 text-[#2C2416]">
                      <IconComponent className="w-5 h-5 text-[#C58B76]" />
                    </div>
                    <h3 className="font-serif-display text-xl text-[#2C2416] mb-2 leading-snug">
                      {vp.title}
                    </h3>
                    <p className="text-sm text-[#5B4A3A] leading-relaxed">
                      {vp.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Hampers Grid */}
      <section id="featured-hampers" className="py-16 md:py-20 bg-[#FAF6F1] border-y border-[#E9DFD3]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.24em] text-[#C58B76] mb-2">
                Curated Hampers
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-[#2C2416]">
                Popular Curations for This Collection
              </h2>
              <p className="text-sm text-[#5B4A3A] mt-2">
                All hampers can be customized with your company logo, custom colors, and personalized cards.
              </p>
            </div>
            <a
              href="#enquire"
              className="text-xs uppercase tracking-widest text-[#7A5C4C] hover:text-[#C58B76] font-semibold"
            >
              Get Custom Quote →
            </a>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {page.featuredProducts.map((p) => (
              <div
                key={p.id}
                id={p.id}
                className="bg-white rounded-2xl overflow-hidden border border-[#EFE3D2] hover:shadow-xl transition-all flex flex-col justify-between group"
              >
                <div>
                  <div className="relative aspect-[4/5] overflow-hidden bg-[#F4E7DA]">
                    <img
                      src={p.img}
                      alt={p.alt || p.name}
                      width="400"
                      height="500"
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {p.tag && (
                      <span className="absolute top-3 left-3 bg-white/90 backdrop-blur text-[#2C2416] text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-medium shadow-sm">
                        {p.tag}
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="text-[11px] uppercase tracking-widest text-[#C58B76] font-medium">
                      {p.category}
                    </div>
                    <h3 className="font-serif-display text-lg text-[#2C2416] mt-1 leading-snug">
                      {p.name}
                    </h3>
                    <p className="text-xs text-[#5B4A3A] mt-2 line-clamp-2 leading-relaxed">
                      {p.desc}
                    </p>
                  </div>
                </div>
                <div className="p-5 pt-0 border-t border-[#F0E7DA] mt-2">
                  <div className="flex items-center justify-between mt-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#7A5C4C]">
                        Bulk Price
                      </div>
                      <div className="font-serif-display text-lg text-[#2C2416] font-semibold">
                        {p.price}
                      </div>
                    </div>
                    <a
                      href="#enquire"
                      className="btn-primary px-4 py-2 rounded-full text-[11px] uppercase tracking-wider inline-flex items-center gap-1.5"
                    >
                      <ShoppingBag className="w-3.5 h-3.5" /> Enquire
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4-Step Process Workflow */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.24em] text-[#C58B76] mb-3">
              How It Works
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#2C2416]">
              Effortless Corporate Gifting in 4 Steps
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                step: '01',
                title: 'Brief & Budget Alignment',
                desc: 'Share your occasion, recipient persona, and target budget. Our gifting stylists send a curated proposal within 24 hours.',
              },
              {
                step: '02',
                title: 'Sample Prototype in 48h',
                desc: 'We courier a physical mock-up kit to your office to inspect box finish, branding quality, and item craftsmanship.',
              },
              {
                step: '03',
                title: 'Precision Co-Branding',
                desc: 'Once approved, we laser-engrave, UV-print, or foil-stamp your logo with strict QA across every individual unit.',
              },
              {
                step: '04',
                title: 'PAN India Doorstep Dispatch',
                desc: 'Delivered securely to company headquarters or individual employee residential addresses with live tracking.',
              },
            ].map((s) => (
              <div
                key={s.step}
                className="p-6 rounded-2xl bg-[#FAF6F1] border border-[#EFE3D2] relative"
              >
                <div className="font-serif-display text-4xl text-[#C58B76]/50 font-bold mb-3">
                  {s.step}
                </div>
                <h3 className="font-serif-display text-xl text-[#2C2416] mb-2">
                  {s.title}
                </h3>
                <p className="text-xs sm:text-sm text-[#5B4A3A] leading-relaxed">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Brand Logos */}
      <BrandsTicker />

      {/* Testimonials with Verified Roles */}
      <Testimonials />

      {/* FAQ Section with FAQPage Schema */}
      <section className="py-16 md:py-24 bg-[#FAF6F1] border-t border-[#E9DFD3]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.24em] text-[#C58B76] mb-3">
              Frequently Asked Questions
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#2C2416]">
              Everything You Need to Know
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full bg-white rounded-2xl p-6 md:p-8 border border-[#EFE3D2]">
            {page.faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-b border-[#EFE3D2] last:border-b-0 py-2"
              >
                <AccordionTrigger className="text-left font-medium text-[#2C2416] hover:no-underline hover:text-[#C58B76] py-4 text-base">
                  <span className="flex items-center gap-3">
                    <HelpCircle className="w-4 h-4 text-[#C58B76] flex-shrink-0" />
                    {f.q}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-[#5B4A3A] leading-relaxed pb-4 pl-7 text-sm">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Pre-Attributed Contact Form */}
      <ContactForm source={page.slug} />

      {/* Internal Linking Silo Hub */}
      <section className="py-14 bg-white border-t border-[#E9DFD3]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h3 className="font-serif-display text-2xl text-[#2C2416]">
              Explore Related Corporate Gifting Hubs &amp; Hampers
            </h3>
            <p className="text-xs uppercase tracking-widest text-[#7A5C4C] mt-1">
              Curated collections for every occasion, team milestone &amp; regional hub
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {otherPages.map((op) => (
              <Link
                key={op.slug}
                to={op.canonicalPath}
                className="p-3.5 rounded-xl bg-[#FAF6F1] hover:bg-[#F4D5C5] border border-[#EFE3D2] hover:border-[#C58B76] transition-colors text-xs font-medium text-[#2C2416] text-center"
              >
                {op.badge}: {op.slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default SeoLandingPage;
